import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Link,
  ImageList,
  ImageListItem,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack,
  ArrowForwardIos,
  ArrowBackIos,
  OpenInNew,
  DirectionsCar as CarIcon,
  TrendingDown as TrendingDownIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../index';
import FavoriteButton from '../components/FavoriteButton';

// Fix default marker icon (leaflet CSS/webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [vehicle, setVehicle] = useState(null);
  const [cheaperMarkets, setCheaperMarkets] = useState([]);
  const [cheaperMarketsSearched, setCheaperMarketsSearched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhotoIdx, setSelectedPhotoIdx] = useState(null);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const photoCountRef = React.useRef(0);

  // Keyboard navigation for photo lightbox
  React.useEffect(() => {
    if (selectedPhotoIdx === null) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft' && selectedPhotoIdx > 0) {
        setSelectedPhotoIdx((i) => i - 1);
      } else if (e.key === 'ArrowRight' && selectedPhotoIdx < photoCountRef.current - 1) {
        setSelectedPhotoIdx((i) => i + 1);
      } else if (e.key === 'Escape') {
        setSelectedPhotoIdx(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedPhotoIdx]);

  // Recalculate cheaper markets relative to THIS vehicle's price, not the local median.
  // A market is only "better value" if its median is below this vehicle's actual price.
  const vehicleMarkets = useMemo(() => {
    if (!vehicle?.price || vehicle.price <= 0 || cheaperMarkets.length === 0) return cheaperMarkets;
    return cheaperMarkets
      .map((m) => {
        const netSavings = Math.round(vehicle.price - m.median_price - (m.transport_cost || 0));
        return { ...m, net_savings: netSavings };
      })
      .filter((m) => m.net_savings > 0)
      .sort((a, b) => b.net_savings - a.net_savings);
  }, [vehicle, cheaperMarkets]);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const vehicleDoc = await getDoc(doc(db, 'vehicles', id));
        if (vehicleDoc.exists()) {
          const data = { id: vehicleDoc.id, ...vehicleDoc.data() };

          // Merge market_analysis and cheaper_markets from cached search results (not in Firestore)
          try {
            const cached = JSON.parse(sessionStorage.getItem('vehicleSearch') || '{}');
            const match = (cached.results || []).find((v) => v.id === id);
            if (match?.market_analysis) {
              data.market_analysis = match.market_analysis;
              data.market_value = match.market_value;
              data.market_savings = match.market_savings;
              data.market_confidence = match.market_confidence;
            }
            // Carry search context (zip, radius) for display
            if (cached.filters?.zipCode) {
              data.search_zip = cached.filters.zipCode;
              data.search_radius = cached.filters.radius;
            }
            // Load cheaper markets from search-level cache
            if (cached.cheaperMarketsSearched) {
              setCheaperMarketsSearched(true);
              setCheaperMarkets(cached.cheaperMarkets || []);
            }
          } catch { /* ignore */ }

          setVehicle(data);
        } else {
          setError('Vehicle not found');
        }
      } catch (err) {
        console.error('Error fetching vehicle:', err);
        setError('Failed to load vehicle details');
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !vehicle) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography color="error" variant="h5">
          {error || 'Vehicle not found'}
        </Typography>
      </Box>
    );
  }

  const photos = vehicle.photo_urls || [];
  photoCountRef.current = photos.length;
  const primaryPhoto = vehicle.photo_url || photos[0] || null;

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to results
      </Button>

      {/* Title bar */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4">
              {vehicle.heading || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5, alignItems: 'center' }}>
              {vehicle.price > 0 && (
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ${vehicle.price.toLocaleString()}
                </Typography>
              )}
              {vehicle.mileage > 0 && (
                <Typography variant="body1" color="text.secondary">
                  {vehicle.mileage.toLocaleString()} mi
                </Typography>
              )}
              {(vehicle.city || vehicle.state) && (
                <Typography variant="body1" color="text.secondary">
                  {[vehicle.city, vehicle.state].filter(Boolean).join(', ')}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FavoriteButton vehicleId={vehicle.id} size="medium" />
            {vehicle.is_certified === 1 && (
              <Chip label="CPO" color="info" size="medium" />
            )}
            {vehicle.arbitrage_score && (
              <Chip
                label={`Score: ${vehicle.arbitrage_score}`}
                color={vehicle.arbitrage_score >= 80 ? 'success' : vehicle.arbitrage_score >= 50 ? 'warning' : 'default'}
                size="medium"
              />
            )}
          </Box>
        </Box>

        {/* Summary cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {/* Market Analysis summary */}
          {vehicle.market_analysis && vehicle.market_savings != null && (
            <Paper
              variant="outlined"
              sx={{ p: 1.5, flex: '1 1 200px', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => document.getElementById('market-analysis')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <Typography variant="overline" color="text.secondary">
                Market Analysis{vehicle.search_zip ? ` — searched near ${vehicle.search_zip}` : ''}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="h6"
                  color={vehicle.market_savings > 0 ? 'success.main' : vehicle.market_savings < 0 ? 'error.main' : 'text.primary'}
                  fontWeight="bold"
                >
                  {vehicle.market_savings > 0 ? '+' : ''}${vehicle.market_savings.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  vs ${vehicle.market_value?.toLocaleString()} market value
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {vehicle.market_confidence} confidence &middot; {vehicle.market_analysis.all?.count || 0} comps
                {vehicle.search_radius ? ` within ${vehicle.search_radius} mi` : ''} &middot;{' '}
                <Link component="span" underline="hover" color="primary">View details</Link>
              </Typography>
            </Paper>
          )}

          {/* Better Value Markets summary */}
          {cheaperMarketsSearched && (
            vehicleMarkets.length > 0 ? (
              <Paper
                variant="outlined"
                sx={{ p: 1.5, flex: '1 1 200px', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => document.getElementById('better-value-markets')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Typography variant="overline" color="text.secondary">
                  Better Value Markets
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h6" color="success.main" fontWeight="bold">
                    Save ${vehicleMarkets[0].net_savings?.toLocaleString()} est.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    in {vehicleMarkets[0].city && vehicleMarkets[0].state
                      ? `${vehicleMarkets[0].city}, ${vehicleMarkets[0].state}`
                      : `Area ${vehicleMarkets[0].zip3}`}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {vehicleMarkets.length} better value market{vehicleMarkets.length !== 1 ? 's' : ''} found &middot;{' '}
                  <Link component="span" underline="hover" color="primary">View details</Link>
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 200px' }}>
                <Typography variant="overline" color="text.secondary">
                  Market Comparison
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Competitively priced in your area — no better value markets found.
                </Typography>
              </Paper>
            )
          )}

        </Box>
      </Paper>

      {/* Photo section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {primaryPhoto ? (
          <Box>
            {/* Primary image */}
            <Box
              component="img"
              src={primaryPhoto}
              alt={vehicle.heading || 'Vehicle'}
              onClick={() => setSelectedPhotoIdx(0)}
              sx={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'contain',
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: 'background.paper',
              }}
            />
            {/* Thumbnail strip */}
            {photos.length > 1 && (() => {
              const mobileCols = Math.min(photos.length, 3);
              const desktopCols = Math.min(photos.length, 8);
              const cols = isMobile ? mobileCols : desktopCols;
              const visibleCount = showAllPhotos ? photos.length : Math.min(photos.length, cols * 2);
              const hasMore = photos.length > cols * 2;
              return (<>
                <ImageList
                  sx={{ mt: 1 }}
                  cols={cols}
                  rowHeight={isMobile ? 100 : 80}
                  gap={6}
                >
                  {photos.slice(0, visibleCount).map((url, idx) => (
                    <ImageListItem
                      key={idx}
                      onClick={() => setSelectedPhotoIdx(idx)}
                      sx={{ cursor: 'pointer', borderRadius: 1, overflow: 'hidden' }}
                    >
                      <img
                        src={url}
                        alt={`Photo ${idx + 1}`}
                        style={{ objectFit: 'cover', height: '100%' }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {photos.length} photos
                  </Typography>
                  {hasMore && !showAllPhotos && (
                    <Button size="small" onClick={() => setShowAllPhotos(true)} sx={{ fontSize: '0.75rem' }}>
                      View all {photos.length} photos
                    </Button>
                  )}
                  {showAllPhotos && hasMore && (
                    <Button size="small" onClick={() => setShowAllPhotos(false)} sx={{ fontSize: '0.75rem' }}>
                      Show less
                    </Button>
                  )}
                </Box>
              </>);
            })()}

          </Box>
        ) : (
          <Box
            sx={{
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <CarIcon sx={{ fontSize: 64, color: 'grey.400' }} />
            <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
              No photos available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Lightbox dialog with prev/next */}
      <Dialog
        open={selectedPhotoIdx !== null}
        onClose={() => setSelectedPhotoIdx(null)}
        maxWidth="lg"
      >
        {selectedPhotoIdx !== null && photos[selectedPhotoIdx] && (
          <Box sx={{ position: 'relative', bgcolor: 'black' }}>
            <Box
              component="img"
              src={photos[selectedPhotoIdx]}
              alt="Vehicle"
              sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
            />
            {/* Prev button */}
            {selectedPhotoIdx > 0 && (
              <Button
                onClick={() => setSelectedPhotoIdx((i) => i - 1)}
                sx={{
                  position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                  minWidth: 40, height: 40, borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                <ArrowBackIos fontSize="small" />
              </Button>
            )}
            {/* Next button */}
            {selectedPhotoIdx < photos.length - 1 && (
              <Button
                onClick={() => setSelectedPhotoIdx((i) => i + 1)}
                sx={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  minWidth: 40, height: 40, borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                <ArrowForwardIos fontSize="small" />
              </Button>
            )}
            {/* Counter */}
            <Typography
              variant="caption"
              sx={{
                position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                color: 'white', bgcolor: 'rgba(0,0,0,0.5)', px: 1.5, py: 0.5, borderRadius: 1,
              }}
            >
              {selectedPhotoIdx + 1} / {photos.length}
            </Typography>
          </Box>
        )}
      </Dialog>

      {/* Details grid */}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Vehicle Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Vehicle Info</Typography>
            <InfoRow label="Year" value={vehicle.year} />
            <InfoRow label="Make" value={vehicle.make} />
            <InfoRow label="Model" value={vehicle.model} />
            <InfoRow label="Trim" value={vehicle.trim} />
            <InfoRow label="Body Type" value={vehicle.body_type} />
            <InfoRow label="Drivetrain" value={vehicle.drivetrain} />
            <InfoRow label="Transmission" value={vehicle.transmission} />
            <InfoRow label="Engine" value={vehicle.engine} />
            <InfoRow label="Fuel Type" value={vehicle.fuel_type} />
            <InfoRow label="Exterior Color" value={vehicle.exterior_color} />
            <InfoRow label="Interior Color" value={vehicle.interior_color} />
            <InfoRow label="VIN" value={vehicle.vin} />
            <InfoRow label="Stock #" value={vehicle.stock_no} />
          </Grid>

          {/* Pricing & Market */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Pricing & Market</Typography>
            <InfoRow
              label="Listing Price"
              value={vehicle.price ? `$${vehicle.price.toLocaleString()}` : null}
            />
            <InfoRow
              label="MSRP"
              value={vehicle.msrp ? `$${vehicle.msrp.toLocaleString()}` : null}
            />
            {vehicle.price && vehicle.msrp && (
              <InfoRow
                label="Below MSRP"
                value={`$${(vehicle.msrp - vehicle.price).toLocaleString()}`}
                color={vehicle.msrp - vehicle.price > 0 ? 'success.main' : 'error.main'}
              />
            )}
            <InfoRow
              label="Mileage"
              value={vehicle.mileage ? `${vehicle.mileage.toLocaleString()} mi` : null}
            />
            <InfoRow label="Days on Market" value={vehicle.dom} />
            <InfoRow label="Days Active (30d)" value={vehicle.dom_active} />
            <InfoRow label="Type" value={vehicle.inventory_type} />
            <InfoRow label="Seller Type" value={vehicle.seller_type} />
            <InfoRow
              label="Certified (CPO)"
              value={vehicle.is_certified === 1 ? 'Yes' : 'No'}
            />
            <InfoRow label="First Seen" tooltip="When this listing first appeared on the market" value={vehicle.first_seen_at_date ? new Date(vehicle.first_seen_at_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''} />
            <InfoRow label="Last Seen" tooltip="When this listing was last confirmed active" value={vehicle.last_seen_at_date ? new Date(vehicle.last_seen_at_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''} />
          </Grid>

          {/* Market Analysis — Comp Breakdown */}
          {vehicle.market_analysis && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }} id="market-analysis">
                Market Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                We compared this listing against similar {vehicle.year} {vehicle.make} {vehicle.model} vehicles
                currently for sale nationwide. Each row below narrows the comparison — from all
                comparable listings down to vehicles matching this one's trim, year, and mileage.
                {vehicle.market_savings != null && vehicle.market_confidence && vehicle.market_confidence !== 'none' && (
                  vehicle.market_savings > 0
                    ? <> This vehicle is priced <strong>${vehicle.market_savings.toLocaleString()} below</strong> the
                      estimated market value of <strong>${vehicle.market_value?.toLocaleString()}</strong> ({vehicle.market_confidence} confidence).</>
                    : vehicle.market_savings < 0
                    ? <> This vehicle is priced <strong>${Math.abs(vehicle.market_savings).toLocaleString()} above</strong> the
                      estimated market value of <strong>${vehicle.market_value?.toLocaleString()}</strong> ({vehicle.market_confidence} confidence).</>
                    : <> This vehicle is priced at the estimated market value of <strong>${vehicle.market_value?.toLocaleString()}</strong>.</>
                )}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Comp Set</TableCell>
                      <TableCell align="right">Median Price</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Positive = below market (good deal)">
                          <span>Savings</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center"># Comps</TableCell>
                      <TableCell align="center">States</TableCell>
                      <TableCell align="center">Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <CompRow label="All comps" data={vehicle.market_analysis.all} />
                    <CompRow label="Similar year (±2yr)" data={vehicle.market_analysis.similarYear} />
                    <CompRow label="Similar mileage (±30k)" data={vehicle.market_analysis.similarMileage} />
                    <CompRow label="Same trim" data={vehicle.market_analysis.sameTrim} />
                    <CompRow label="Best match" data={vehicle.market_analysis.bestMatch} highlight />
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Better Value Markets */}
          {cheaperMarketsSearched && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }} id="better-value-markets">
                <TrendingDownIcon color={vehicleMarkets.length > 0 ? 'success' : 'action'} /> Better Value Markets
              </Typography>
              {vehicleMarkets.length > 0 ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Based on nationwide market data, similar {vehicle.year} {vehicle.make} {vehicle.model} vehicles
                    may be available for less in other areas. Savings are compared to this vehicle's price and include estimated transport.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {vehicleMarkets.slice(0, 5).map((m, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5, flex: '1 1 200px', maxWidth: 280 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {m.city && m.state ? `${m.city}, ${m.state}` : `Area ${m.zip3}xx`}
                        </Typography>
                        {m.city && m.state && (
                          <Typography variant="caption" color="text.secondary">
                            ZIP: {m.zip3}xx
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Median: ${m.median_price?.toLocaleString()} ({m.sample_size} listings)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <LocalShippingIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            ${m.transport_cost?.toLocaleString()} est. transport
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold" color="success.main" sx={{ mt: 0.5 }}>
                          Save ${m.net_savings?.toLocaleString()} est. net
                        </Typography>
                        <Button
                          size="small"
                          endIcon={<OpenInNew fontSize="small" />}
                          onClick={() => {
                            const params = new URLSearchParams();
                            params.set('make', vehicle.make);
                            if (vehicle.model) params.set('model', vehicle.model);
                            params.set('yearMin', vehicle.year);
                            params.set('yearMax', vehicle.year);
                            params.set('zipCode', m.zip3 + '01');
                            params.set('radius', '100');
                            window.open(`/?${params.toString()}`, '_blank');
                          }}
                          sx={{ mt: 1, p: 0, minWidth: 0, textTransform: 'none' }}
                        >
                          Search this market
                        </Button>
                      </Paper>
                    ))}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No better value markets found — this {vehicle.year} {vehicle.make} {vehicle.model} is competitively priced
                  in your search area compared to other US markets.
                </Typography>
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Dealer Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Dealer Info</Typography>
            <InfoRow label="Dealer" value={vehicle.dealer_name} />
            <InfoRow label="Phone" value={vehicle.dealer_phone} />
            <InfoRow label="Address" value={vehicle.dealer_street} />
            <InfoRow
              label="Location"
              value={
                (() => {
                  const loc = [vehicle.city, vehicle.state, vehicle.zip].filter(Boolean).join(', ');
                  if (!loc) return null;
                  const addr = [vehicle.dealer_street, vehicle.city, vehicle.state, vehicle.zip].filter(Boolean).join(', ');
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
                  return (
                    <>{loc} <Link href={mapsUrl} target="_blank" rel="noopener" sx={{ ml: 0.5, fontSize: 'inherit' }}>View on Map ↗</Link></>
                  );
                })()
              }
            />
            <InfoRow label="Dealer Type" value={vehicle.dealer_type} />
            {vehicle.dealer_rating && (
              <InfoRow label="Rating" value={`${vehicle.dealer_rating} / 5`} />
            )}
            {vehicle.latitude && vehicle.longitude && (
              <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <MapContainer
                  center={[vehicle.latitude, vehicle.longitude]}
                  zoom={13}
                  style={{ height: 220, width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[vehicle.latitude, vehicle.longitude]}>
                    <Popup>
                      {vehicle.dealer_name || 'Dealer Location'}<br />
                      {[vehicle.city, vehicle.state].filter(Boolean).join(', ')}
                    </Popup>
                  </Marker>
                </MapContainer>
              </Box>
            )}
          </Grid>

          {/* Links */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Links</Typography>
            {vehicle.vdp_url ? (
              <Box sx={{ py: 0.5 }}>
                <Link
                  href={vehicle.vdp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  View Original Listing <OpenInNew fontSize="small" />
                </Link>
              </Box>
            ) : (
              <InfoRow label="Listing URL" value={null} />
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

function CompRow({ label, data, highlight }) {
  if (!data || data.count === 0) {
    return (
      <TableRow>
        <TableCell>{label}</TableCell>
        <TableCell align="right" colSpan={5}>
          <Typography variant="body2" color="text.secondary">No comps</Typography>
        </TableCell>
      </TableRow>
    );
  }

  const savingsColor = data.savings > 0 ? 'success.main' : data.savings < 0 ? 'error.main' : 'text.primary';

  return (
    <TableRow sx={highlight ? { bgcolor: 'action.hover' } : undefined}>
      <TableCell>
        <Typography variant="body2" fontWeight={highlight ? 'bold' : 'normal'}>
          {label}
        </Typography>
      </TableCell>
      <TableCell align="right">${data.median?.toLocaleString()}</TableCell>
      <TableCell align="right">
        <Typography variant="body2" color={savingsColor} fontWeight="medium">
          {data.savings > 0 ? '+' : ''}{data.savings !== null ? `$${data.savings.toLocaleString()}` : '-'}
        </Typography>
      </TableCell>
      <TableCell align="center">{data.count}</TableCell>
      <TableCell align="center">{data.states}</TableCell>
      <TableCell align="center">
        <Chip
          label={data.confidence}
          size="small"
          color={
            data.confidence === 'high' ? 'success'
              : data.confidence === 'medium' ? 'warning'
              : 'default'
          }
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
      </TableCell>
    </TableRow>
  );
}

function InfoRow({ label, value, color, tooltip }) {
  const labelContent = (
    <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0, cursor: tooltip ? 'help' : 'default' }}>
      {label}:
    </Typography>
  );
  return (
    <Box sx={{ display: 'flex', py: 0.5 }}>
      {tooltip ? <Tooltip title={tooltip} arrow>{labelContent}</Tooltip> : labelContent}
      <Typography variant="body2" fontWeight="medium" color={color || 'text.primary'}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );
}

export default VehicleDetail;
